import {
  IProgrammingGanttRow,
  IProgrammingGanttSchedule,
  TProgrammingGanttContextAction,
} from "@/components/programming-gantt/programming-gantt";
import { TSectionPlanning } from "@/data/api/kili/sites/types/planning.type";
import {
  ECommonProgramMetaMode,
  EHeaterProgramMetaMode,
  EProgrammingMetaMode,
  TProgramMetaMode,
} from "@/domains/v4b/program/subprogram.types";
import i18n from "@/i18n";
import $date, { EDateScaling } from "@/utils/date";

export interface IProgrammingSchedule {
  id: string;
  startDate: Date;
  endDate: Date;
  mode: EHeaterProgramMetaMode;
}

export interface IProgrammingDevice {
  id: string;
  name: string;
  schedule: Array<IProgrammingSchedule>;
}

export interface ISelectedSchedule {
  planning: TSectionPlanning;
  initialSchedule: IProgrammingDeviceSchedule;
}

function generateUniqueId(): string {
  return Math.random().toString(36).substr(2, 9);
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IProgrammingDeviceRow extends IProgrammingGanttRow {}

export interface IProgrammingDeviceSchedule extends IProgrammingGanttSchedule {
  type: TProgramMetaMode;
  temperature?: number;
}

export function transformPlanningToGanttData(planning: TSectionPlanning): {
  row: IProgrammingDeviceRow;
  schedules: Array<IProgrammingDeviceSchedule>;
} {
  const intl = i18n.global;

  const rowId = `${planning.sectionId}`;

  const row: IProgrammingDeviceRow = {
    id: rowId,
    name: planning.sectionName,
  };

  const schedules: IProgrammingDeviceSchedule[] = [];
  let previousEnd: Date | null = null;

  const overrides = [...(planning?.ranges ?? [])];

  const scheduleContextMenu = [
    {
      type: "ACTION",
      id: "EDIT",
      label: intl.t("global.misc.edit"),
      icon: "pi-pencil",
    },
    {
      type: "DIVIDER",
    },
    {
      type: "ACTION",
      id: "DELETE",
      label: intl.t("global.misc.delete"),
      icon: "pi-trash",
      color: "red",
    },
  ] satisfies TProgrammingGanttContextAction[];

  if (!overrides.length) {
    schedules.push({
      id: generateUniqueId(),
      rowId: rowId,
      start: $date.subtract(new Date(), 100, EDateScaling.YEARS),
      end: $date.add(new Date(), 100, EDateScaling.YEARS),
      type: EProgrammingMetaMode.PROGRAMMING,
      realStart: $date.subtract(new Date(), 100, EDateScaling.YEARS),
      realEnd: $date.add(new Date(), 100, EDateScaling.YEARS),
      contextMenu: [],
    });
  } else {
    overrides.forEach((schedule) => {
      schedules.push({
        id: generateUniqueId(),
        rowId: rowId,
        start: new Date(schedule.startTime.getTime()),
        end: new Date(schedule.endTime.getTime()),
        type: schedule.metaMode,
        realStart: schedule.startTime,
        realEnd: schedule.endTime,
        temperature: schedule.metaMode === ECommonProgramMetaMode.TEMP ? schedule.temperature : undefined,
        contextMenu: scheduleContextMenu,
      });

      if (previousEnd) {
        const diff = schedule.startTime.getTime() - previousEnd.getTime();

        // Avoid having weird programming for two almost identical schedules
        if (diff > 1000) {
          schedules.push({
            id: generateUniqueId(),
            rowId: rowId,
            start: new Date(previousEnd.getTime()),
            end: new Date(schedule.startTime.getTime()),
            type: EProgrammingMetaMode.PROGRAMMING,
            realStart: schedule.startTime,
            realEnd: schedule.endTime,
            temperature: schedule.metaMode === ECommonProgramMetaMode.TEMP ? schedule.temperature : undefined,
            contextMenu: [],
          });
        }
      }

      previousEnd = schedule.endTime;
    });

    const firstStartTime = overrides[0]?.startTime ?? $date.add(new Date(), 100, EDateScaling.YEARS);
    schedules.unshift({
      id: generateUniqueId(),
      rowId: rowId,
      start: $date.subtract(new Date(), 100, EDateScaling.YEARS),
      end: new Date(firstStartTime.getTime()),
      type: EProgrammingMetaMode.PROGRAMMING,
      realStart: $date.subtract(new Date(), 100, EDateScaling.YEARS),
      realEnd: firstStartTime,
      contextMenu: [],
    });

    const lastEndTime = overrides[overrides.length - 1]?.endTime ?? $date.subtract(new Date(), 100, EDateScaling.YEARS);
    schedules.push({
      id: generateUniqueId(),
      rowId: rowId,
      start: new Date(lastEndTime.getTime()),
      end: $date.add(new Date(), 100, EDateScaling.YEARS),
      type: EProgrammingMetaMode.PROGRAMMING,
      realStart: lastEndTime,
      realEnd: $date.add(new Date(), 100, EDateScaling.YEARS),
      contextMenu: [],
    });
  }

  return { row, schedules };
}

export function generateGanttData(plannings: Array<TSectionPlanning>) {
  const rows: IProgrammingDeviceRow[] = [];
  const schedules: IProgrammingDeviceSchedule[] = [];

  for (const planning of plannings) {
    const { row, schedules: deviceSchedules } = transformPlanningToGanttData(planning);
    rows.push(row);
    schedules.push(...deviceSchedules);
  }

  return {
    rows,
    schedules,
  };
}
